import { Platform, Dimensions, type ViewStyle, type TextStyle } from 'react-native';

/**
 * Platform detection utilities for web/mobile differentiation
 */

// Check if running on web
export const isWeb = Platform.OS === 'web';
export const isNative = Platform.OS === 'ios' || Platform.OS === 'android';
export const isIOS = Platform.OS === 'ios';
export const isAndroid = Platform.OS === 'android';

// Breakpoints for responsive design (web)
export const breakpoints = {
  sm: 640,   // Mobile landscape
  md: 768,   // Tablet
  lg: 1024,  // Desktop
  xl: 1280,  // Large desktop
  '2xl': 1536, // Extra large
} as const;

// Get current window dimensions
export const getWindowDimensions = () => {
  const { width, height } = Dimensions.get('window');
  return { width, height };
};

// Check if screen is at least a certain breakpoint (for web)
export const isAtLeast = (breakpoint: keyof typeof breakpoints): boolean => {
  if (!isWeb) return false;
  const { width } = getWindowDimensions();
  return width >= breakpoints[breakpoint];
};

// Check if we should use desktop layout
export const useDesktopLayout = (): boolean => {
  return isWeb && isAtLeast('lg');
};

// Check if we should use tablet layout
export const useTabletLayout = (): boolean => {
  return isWeb && isAtLeast('md') && !isAtLeast('lg');
};

// Web-specific design constants
export const webDesign = {
  // Sidebar width
  sidebarWidth: 260,
  sidebarCollapsedWidth: 72,

  // Header height
  headerHeight: 64,

  // Content max width for readability
  contentMaxWidth: 1200,

  // Card shadows for depth
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1)',
  },

  // Hover states
  hoverOpacity: 0.9,
  hoverScale: 1.02,

  // Transition durations
  transitions: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
  },
} as const;

/**
 * Type-safe helper for web-only CSS properties that React Native's ViewStyle
 * doesn't include (outline, cursor, calc widths, etc.).
 *
 * Uses Platform.select so the cast is centralized here instead of scattered
 * across dozens of components.
 */
export function webStyle<T extends ViewStyle | TextStyle>(
  nativeStyle: T,
  webExtras: Record<string, unknown>,
): T {
  return Platform.select({
    web: { ...nativeStyle, ...webExtras } as T,
    default: nativeStyle,
  }) as T;
}

/**
 * Shortcut: merge a normal style object with web-only `outline: 'none'`,
 * the single most common web-only override in the codebase.
 */
export function noOutlineStyle<T extends ViewStyle | TextStyle>(style: T): T {
  return webStyle(style, { outline: 'none' });
}

// Mobile-specific design constants
export const mobileDesign = {
  // Bottom tab bar height
  tabBarHeight: 85,

  // Safe area defaults
  safeAreaTop: 44,
  safeAreaBottom: 34,

  // Touch target minimum size
  minTouchTarget: 44,
} as const;
